import { useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";

const rs = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

/* Cost price changed in the last 7 days → small amber badge with old → new. */
export const ChangeBadge = ({ ch, testid }) => {
  if (!ch) return null;
  const up = Number(ch.new) > Number(ch.old);
  return (
    <span title={`Cost changed on ${new Date(ch.at).toLocaleDateString()}`} className={`ml-1.5 inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-bold ${up ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"}`} data-testid={testid}>
      Price {up ? "↑" : "↓"} {rs(ch.old)} → {rs(ch.new)}
    </span>
  );
};

/* Fill a dropship client's sale prices in one tap: cost + % or + ₹, fill empty only or overwrite all. */
export const MarkupBar = ({ client, q, changed, onDone }) => {
  const [mode, setMode] = useState("percent");
  const [val, setVal] = useState("30");
  const [scope, setScope] = useState("empty");
  const [busy, setBusy] = useState(false);
  const body = { client_id: client.id, mode, value: val, scope, q, changed };
  const apply = async () => {
    setBusy(true);
    try {
      const dry = await papi("post", "/pricelist/markup", { ...body, dry: true });
      if (!dry.set) { toast.info(scope === "empty" ? "Every product here already has a sale price" : "No products with a cost price here"); return; }
      const what = `${mode === "percent" ? `cost + ${val}%` : `cost + ₹${val}`}`;
      if (!window.confirm(`Set ${dry.set} sale price(s) for ${client.name} to ${what} (rounded up to the rupee)?${dry.skipped ? `\n${dry.skipped} product(s) that already have a price stay as they are.` : ""}`)) return;
      const r = await papi("post", "/pricelist/markup", body);
      toast.success(`${r.set} sale price(s) set for ${client.name}`); onDone();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-indigo-200 bg-white p-2.5 text-xs" data-testid="pl-markup">
      <Wand2 className="h-4 w-4 text-indigo-600" /><b className="text-indigo-900">Bulk fill sale price</b>
      <span className="text-slate-500">= cost +</span>
      <input value={val} onChange={(e) => setVal(e.target.value)} inputMode="decimal" className="h-8 w-16 rounded-lg border px-2 text-right text-sm" data-testid="pl-markup-value" />
      <select value={mode} onChange={(e) => setMode(e.target.value)} className="h-8 rounded-lg border bg-white px-2 font-semibold" data-testid="pl-markup-mode">
        <option value="percent">%</option><option value="fixed">₹ fixed</option></select>
      <select value={scope} onChange={(e) => setScope(e.target.value)} className="h-8 rounded-lg border bg-white px-2 font-semibold" data-testid="pl-markup-scope">
        <option value="empty">Fill empty only</option><option value="all">Overwrite all</option></select>
      <button onClick={apply} disabled={busy || val === "" || Number.isNaN(Number(val)) || Number(val) < 0} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 font-semibold text-white disabled:opacity-50" data-testid="pl-markup-apply">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Apply</button>
      <span className="w-full text-[11px] text-slate-400">Applies to {changed ? "the changed products" : q ? `products matching “${q}”` : "every product in the list"}.</span>
    </div>
  );
};
